import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Send, Plus, Mail, MessageSquare, Phone as PhoneIcon, Truck,
  X, Search, Sparkles, Eye, Check, Loader2, CheckCircle2,
  AlertCircle, Calendar, ArrowRight, RefreshCw, Inbox, Lock, TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/datetime";
import { useAuth } from "@/context/AuthContext";
import { isPaidTier } from "@/lib/tiers";

const CHANNELS = [
  { value: "email",       label: "Email",       icon: Mail,           accent: "emerald" },
];

const CHANNEL_META = Object.fromEntries(CHANNELS.map((c) => [c.value, c]));

const STATUS_META = {
  queued:         { label: "Queued",          color: "text-slate-300",  dot: "bg-slate-400" },
  sent:           { label: "Sent",            color: "text-sky-300",    dot: "bg-sky-400" },
  failed:         { label: "Failed",          color: "text-rose-300",   dot: "bg-rose-400" },
  replied:        { label: "Replied",         color: "text-emerald-300",dot: "bg-emerald-400" },
  meeting_booked: { label: "Meeting Booked",  color: "text-violet-300", dot: "bg-violet-400" },
  skipped:        { label: "Skipped",         color: "text-amber-300",  dot: "bg-amber-400" },
};

const TEMPLATES_BY_CHANNEL = {
  email: [
    {
      name: "Seller financing intro",
      body: `Subject: Quick question about {title}

Hi {owner_first_name},

I came across your listing for {title} in {city}, {state}. We're acquiring assets in your market and can move quickly with flexible structures, including seller financing with attractive terms for the right deal.

Asking is {asking_price}. Would you be open to a 10-minute call this week to explore whether the structure fits?

Best,
{your_name}`,
    },
    {
      name: "Master lease pitch",
      body: `Subject: Master lease option for {title}

Hi {owner_first_name},

Rather than a traditional sale, would you consider a master lease with an option to purchase for {title} in {city}? We'd take over operations, keep your cash flow steady, and lock in a future buyout with minimal disruption for you.

Open to a brief call?

Best,
{your_name}`,
    },
  ],
  voicemail: [
    {
      name: "Acquisition script (30s)",
      body: `Hi {owner_first_name}, this is {your_name} calling about your {title} property in {city}. We acquire creatively, including seller financing and master-lease structures, and we're interested in exploring whether your situation fits. No pressure, just a conversation. Please call me back at {your_phone}. Thanks, {owner_first_name}.`,
    },
  ],
  direct_mail: [
    {
      name: "Owner letter",
      body: `Dear {owner_name},

We are actively acquiring properties like {title} in {city}, {state}, and we would welcome the chance to discuss a flexible offer with you.

We can structure on seller financing, master lease with option, or a creative installment sale, whichever fits your timeline and tax picture. Many sellers prefer this over a traditional listing because it preserves income and defers tax.

If this sounds worth a conversation, please call me directly at {your_phone}.

Sincerely,
{your_name}`,
    },
  ],
};

const MERGE_TAGS = [
  "owner_name", "owner_first_name", "title", "city", "state",
  "asking_price", "annual_revenue", "annual_cash_flow",
  "your_name", "your_phone",
];

export default function Outreach() {
  const { user } = useAuth();
  const isPaid = !!user && (
    ["admin", "super_admin"].includes(user.role) || isPaidTier(user.tier)
  );

  const [campaigns, setCampaigns] = useState([]);
  const [opps, setOpps] = useState([]);
  const [show, setShow] = useState(false);
  const [openCid, setOpenCid] = useState(null);
  const [loading, setLoading] = useState(true);

  // Iter 58: Ready-to-Contact queue
  const [readyQueue, setReadyQueue] = useState([]);
  const [readyLoading, setReadyLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [approving, setApproving] = useState(false);
  // BLOCK 100 E-7: contact-hour override modal target { oid, window }
  const [chOverride, setChOverride] = useState(null);

  const loadReadyQueue = useCallback(async () => {
    if (!isPaid) return;
    setReadyLoading(true);
    try {
      const r = await api.get("/outreach-queue/ready");
      setReadyQueue(r.data.items || []);
    } catch {
      // don't toast ,  silent failure keeps the page usable
    } finally {
      setReadyLoading(false);
    }
  }, [isPaid]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, o] = await Promise.all([
        api.get("/outreach"),
        api.get("/opportunities", { params: { limit: 500 } }),
      ]);
      setCampaigns(c.data || []);
      setOpps(o.data || []);
    } catch {
      toast.error("Failed to load campaigns");
    } finally {
      setLoading(false);
    }
    loadReadyQueue();
  }, [loadReadyQueue]);

  useEffect(() => { load(); }, [load]);

  const toggleSelect = (oid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(oid)) next.delete(oid);
      else next.add(oid);
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (selected.size === readyQueue.length) setSelected(new Set());
    else setSelected(new Set(readyQueue.map((o) => o.oid)));
  };

  const approveOne = async (oid, override = null) => {
    setApproving(true);
    try {
      const body = override
        ? { override_contact_hours: true, attestation: true, justification: override.justification }
        : {};
      await api.post(`/outreach-queue/approve/${oid}`, body);
      toast.success(override ? "Override attested ,  outreach approved & fired" : "Outreach approved & fired");
      setChOverride(null);
      setSelected((prev) => { const n = new Set(prev); n.delete(oid); return n; });
      loadReadyQueue();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409 && detail?.error === "outside_contact_hours") {
        setChOverride({ oid, window: detail.window });
        return;
      }
      const reason = (typeof detail === "string" ? detail : detail?.error) || "Approve failed";
      toast.error(reason);
    } finally {
      setApproving(false);
    }
  };

  const approveSelected = async () => {
    if (selected.size === 0) return;
    setApproving(true);
    try {
      const r = await api.post("/outreach-queue/approve-bulk", { oids: Array.from(selected) });
      const s = r.data.summary;
      toast.success(`Approved ${s.fired} of ${s.total}`);
      if (s.outside_hours > 0) {
        toast.warning(`${s.outside_hours} lead${s.outside_hours === 1 ? "" : "s"} outside legal contact hours ,  approve individually to attest an override.`);
      }
      setSelected(new Set());
      loadReadyQueue();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk approve failed");
    } finally {
      setApproving(false);
    }
  };

  const stats = useMemo(() => {
    const acc = Object.fromEntries(CHANNELS.map((c) => [c.value, {
      campaigns: 0, sent: 0, replied: 0, meetings: 0,
    }]));
    for (const c of campaigns) {
      if (!acc[c.channel]) continue;
      acc[c.channel].campaigns += 1;
      acc[c.channel].sent += c.sent || 0;
      acc[c.channel].replied += c.replied || 0;
      acc[c.channel].meetings += c.meetings_booked || 0;
    }
    return acc;
  }, [campaigns]);

  // Task 2: Free-tier upgrade gate. Match the pattern used on Assistant.jsx.
  if (user && !isPaid) {
    return (
      <div className="max-w-3xl mx-auto py-16 px-4" data-testid="outreach-upgrade-gate">
        <div className="ae-card p-10 text-center space-y-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-[10px] uppercase tracking-widest">
            <Lock className="h-3 w-3" /> Pro feature
          </div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-400">Module 8</div>
          <h1 className="font-heading text-4xl tracking-tight text-slate-100">Automated Outreach</h1>
          <p className="text-sm text-slate-400 max-w-lg mx-auto">
            Upgrade to Pro to activate automated email outreach.
            Approval-gated sends, reply tracking, and CAN-SPAM safeguards
            built in. Available on <span className="text-emerald-300">Pro and Business</span> plans.
          </p>
          <div className="grid sm:grid-cols-2 gap-3 text-left text-xs text-slate-400 max-w-xl mx-auto pt-2">
            <div className="border border-white/10 rounded-sm p-3">
              <div className="text-emerald-300 text-[10px] uppercase tracking-widest mb-1">BYOK email</div>
              Sends from your own connected Brevo or Resend account.
            </div>
            <div className="border border-white/10 rounded-sm p-3">
              <div className="text-emerald-300 text-[10px] uppercase tracking-widest mb-1">Ready-to-Contact queue</div>
              Every send waits for your approval ,  no blind auto-fires.
            </div>
            <div className="border border-white/10 rounded-sm p-3">
              <div className="text-emerald-300 text-[10px] uppercase tracking-widest mb-1">Reply tracking</div>
              Open, click, and reply signals routed into your pipeline.
            </div>
            <div className="border border-white/10 rounded-sm p-3">
              <div className="text-emerald-300 text-[10px] uppercase tracking-widest mb-1">Compliance built in</div>
              CAN-SPAM footer, DNC screening, and sender-profile enforcement.
            </div>
          </div>
          <Link
            to="/app/billing"
            data-testid="outreach-upgrade-btn"
            className="inline-flex items-center gap-2 btn-primary text-sm"
          >
            Upgrade to Pro <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-2">Module 8</div>
          <h1 className="font-heading text-4xl tracking-tight">Automated Outreach</h1>
          <p className="text-sm text-slate-400 mt-1">Approval-gated email outreach ,  nothing sends without your review. Sends from your own connected Brevo or Resend account.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} data-testid="outreach-refresh-btn" className="btn-ghost text-xs inline-flex items-center gap-1.5">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button onClick={() => setShow(true)} data-testid="new-campaign-btn" className="btn-primary text-sm inline-flex items-center gap-2">
            <Plus className="h-4 w-4" /> New Campaign
          </button>
        </div>
      </header>

      {/* Iter 58: Ready-to-Contact queue */}
      <div className="ae-card p-5 border-emerald-500/30" data-testid="ready-to-contact-panel">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-1 flex items-center gap-2">
              <TrendingUp className="h-3 w-3" /> Ready to Contact
              <span
                className="ml-1 px-1.5 py-0.5 rounded-sm bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-[10px] font-mono"
                data-testid="ready-queue-badge-count"
              >
                {readyQueue.length}
              </span>
            </div>
            <div className="text-xs text-slate-400">
              Leads that crossed the scoring threshold and are waiting for your approval. Nothing sends until you click.
            </div>
          </div>
          <div className="flex items-center gap-2">
            {readyQueue.length > 0 && (
              <>
                <button
                  onClick={toggleSelectAll}
                  data-testid="ready-select-all"
                  className="btn-ghost text-xs"
                >
                  {selected.size === readyQueue.length ? "Deselect all" : "Select all"}
                </button>
                <button
                  onClick={approveSelected}
                  disabled={selected.size === 0 || approving}
                  data-testid="ready-approve-selected"
                  className="btn-primary text-xs inline-flex items-center gap-1.5 disabled:opacity-40"
                >
                  <CheckCircle2 className="h-3.5 w-3.5" /> Approve {selected.size > 0 ? `(${selected.size})` : "selected"}
                </button>
              </>
            )}
            <button onClick={loadReadyQueue} className="btn-ghost text-xs">
              <RefreshCw className={`h-3.5 w-3.5 ${readyLoading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        {readyLoading ? (
          <div className="text-sm text-slate-500 text-center py-6">Loading queue…</div>
        ) : readyQueue.length === 0 ? (
          <div className="text-sm text-slate-500 text-center py-6" data-testid="ready-queue-empty">
            No leads waiting. New qualifying opportunities will appear here after the scoring engine runs.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-white/8">
                  <th className="px-2 py-2 w-8"></th>
                  <th className="text-left px-2 py-2">Opportunity</th>
                  <th className="text-right px-2 py-2">Score</th>
                  <th className="text-left px-2 py-2">Drivers</th>
                  <th className="text-left px-2 py-2">Channel</th>
                  <th className="text-right px-2 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {readyQueue.map((o) => {
                  const drivers = (o.cf_readiness_drivers || []).slice(0, 3);
                  const chan = (o.contact_channels || [])[0] || "email";
                  return (
                    <tr key={o.oid} className="border-b border-white/5" data-testid={`ready-row-${o.oid}`}>
                      <td className="px-2 py-3">
                        <input
                          type="checkbox"
                          checked={selected.has(o.oid)}
                          onChange={() => toggleSelect(o.oid)}
                          className="accent-emerald-500"
                          data-testid={`ready-select-${o.oid}`}
                        />
                      </td>
                      <td className="px-2 py-3">
                        <div className="font-medium text-slate-100 truncate max-w-xs">{o.title}</div>
                        <div className="text-[10px] text-slate-500">{o.city}, {o.state} · {o.asset_type}</div>
                      </td>
                      <td className="px-2 py-3 text-right">
                        <span className="font-mono tabular-nums text-emerald-400 font-semibold">{o.cf_readiness_score}</span>
                      </td>
                      <td className="px-2 py-3 text-xs text-slate-400 max-w-md">
                        {drivers.length > 0 ? drivers.join(" · ") : ", "}
                      </td>
                      <td className="px-2 py-3 text-xs">
                        {(o.contact_channels || []).length > 0 ? (
                          <span className="px-2 py-0.5 rounded-sm bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 font-mono text-[10px]">
                            {chan.toUpperCase()}
                          </span>
                        ) : (
                          <span className="text-slate-500 text-[10px]">no contact</span>
                        )}
                      </td>
                      <td className="px-2 py-3 text-right">
                        <button
                          onClick={() => approveOne(o.oid)}
                          disabled={approving || (o.contact_channels || []).length === 0}
                          data-testid={`ready-approve-${o.oid}`}
                          className="btn-primary text-xs inline-flex items-center gap-1 disabled:opacity-40"
                        >
                          <CheckCircle2 className="h-3 w-3" /> Approve & Contact
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {CHANNELS.map((c) => {
          const I = c.icon;
          const s = stats[c.value];
          const replyRate = s.sent ? Math.round((s.replied / s.sent) * 100) : 0;
          return (
            <div key={c.value} className="ae-card p-4" data-testid={`stat-${c.value}`}>
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-sm bg-emerald-500/10 flex items-center justify-center">
                  <I className="h-4 w-4 text-emerald-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">{c.label}</div>
                  <div className="flex items-baseline gap-2">
                    <div className="font-mono text-xl tabular-nums">{s.sent}</div>
                    <div className="text-[10px] text-slate-500">sent</div>
                  </div>
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    {s.campaigns} campaign{s.campaigns === 1 ? "" : "s"} · {replyRate}% reply
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="ae-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-white/8">
              <th className="text-left px-4 py-3">Campaign</th>
              <th className="text-left px-4 py-3">Channel</th>
              <th className="text-right px-4 py-3">Targets</th>
              <th className="text-right px-4 py-3">Sent</th>
              <th className="text-right px-4 py-3">Replied</th>
              <th className="text-right px-4 py-3">Reply %</th>
              <th className="text-right px-4 py-3">Meetings</th>
              <th className="text-left px-4 py-3">Created</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="text-center py-12 text-slate-500">
                <Loader2 className="h-6 w-6 mx-auto animate-spin text-slate-600" />
              </td></tr>
            ) : campaigns.length === 0 ? (
              <tr><td colSpan={9} className="text-center py-12 text-slate-500">
                <Inbox className="h-8 w-8 mx-auto text-slate-700 mb-2" />
                No campaigns yet. Launch your first outreach above.
              </td></tr>
            ) : campaigns.map((c) => {
              const Channel = CHANNEL_META[c.channel]?.icon || Mail;
              return (
                <tr
                  key={c.cid}
                  data-testid={`campaign-row-${c.cid}`}
                  className="border-b border-white/5 hover:bg-white/[0.03] cursor-pointer"
                  onClick={() => setOpenCid(c.cid)}
                >
                  <td className="px-4 py-3 font-medium">{c.name}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1.5 text-slate-300 capitalize">
                      <Channel className="h-3.5 w-3.5 text-emerald-400" />
                      {c.channel.replace("_", " ")}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums">{c.total || c.target_oids?.length || 0}</td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums">{c.sent}</td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums text-emerald-400">{c.replied}</td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums text-slate-300">
                    {c.reply_rate ? `${c.reply_rate}%` : "N/A"}
                  </td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums text-violet-300">{c.meetings_booked}</td>
                  <td className="px-4 py-3 text-xs text-slate-500 font-mono">{fmtDateTime(c.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <ArrowRight className="h-3.5 w-3.5 text-slate-500 inline-block" />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {show && (
        <CreateCampaign
          opps={opps}
          onClose={() => setShow(false)}
          onCreated={(cid) => { setShow(false); load(); setOpenCid(cid); }}
        />
      )}
      {openCid && (
        <CampaignDetail
          cid={openCid}
          onClose={() => { setOpenCid(null); load(); }}
        />
      )}
      {chOverride && (
        <ContactHoursOverrideModal
          window={chOverride.window}
          busy={approving}
          onCancel={() => setChOverride(null)}
          onConfirm={(justification) => approveOne(chOverride.oid, { justification })}
        />
      )}
    </div>
  );
}

// =====================================================================
// Campaign Detail Drawer
// =====================================================================

function CampaignDetail({ cid, onClose }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);
  // BLOCK 100 E-7: contact-hour override modal for the campaign send path.
  const [chOverride, setChOverride] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/outreach/${cid}`);
      setData(r.data);
    } catch {
      toast.error("Failed to load campaign");
      onClose();
    }
  }, [cid, onClose]);

  useEffect(() => { load(); }, [load]);

  const onSend = async (override = null) => {
    setBusy(true);
    try {
      const body = override
        ? { override_contact_hours: true, attestation: true, justification: override.justification }
        : {};
      const r = await api.post(`/outreach/${cid}/send`, body);
      const d = r.data;
      toast.success(`${d.sent} sent · ${d.failed} failed · ${d.skipped} skipped`);
      if (!override && d.outside_contact_hours > 0) {
        // Some recipients were blocked by the legal contact-hour window.
        setChOverride({ count: d.outside_contact_hours });
      } else {
        setChOverride(null);
      }
      setConfirmSend(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setBusy(false);
    }
  };

  const markStatus = async (rid, status) => {
    try {
      await api.patch(`/outreach/${cid}/recipients/${rid}`, { status });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  if (!data) {
    return (
      <div className="fixed inset-0 z-50 flex justify-end items-start" data-testid="campaign-detail-overlay">
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
        <div className="relative bg-[#05070A] border-l border-white/8 w-full max-w-3xl h-full max-h-[calc(100vh-80px)] flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-slate-600" />
        </div>
      </div>
    );
  }

  const Channel = CHANNEL_META[data.channel]?.icon || Mail;
  const queuedCount = data.recipients.filter((r) => r.status === "queued").length;

  return (
    <div className="fixed inset-0 z-50 flex justify-end items-start" data-testid="campaign-detail-overlay">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div
        className="relative bg-[#05070A] border-l border-white/8 w-full max-w-3xl lg:max-w-[calc(48rem+96px)] h-full max-h-[calc(100vh-80px)] overflow-y-auto"
        data-testid="campaign-detail-drawer"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-white/8 bg-[#05070A]/95 backdrop-blur">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-400 inline-flex items-center gap-1.5">
              <Channel className="h-3 w-3" /> {data.channel.replace("_", " ")} campaign
            </div>
            <div className="font-heading text-xl tracking-tight mt-0.5">{data.name}</div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-100 p-1.5" data-testid="campaign-detail-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Targets" value={data.total} />
            <Stat label="Sent" value={data.sent} accent="sky" />
            <Stat label="Replied" value={data.replied} accent="emerald" />
            <Stat label="Meetings" value={data.meetings_booked} accent="violet" />
          </div>

          {queuedCount > 0 && (
            <div className="ae-card p-4 border-emerald-500/30 bg-emerald-500/5">
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-widest text-emerald-400">Ready to send</div>
                  <div className="text-sm text-slate-200 mt-0.5">
                    {queuedCount} recipient{queuedCount === 1 ? "" : "s"} queued for {data.channel.replace("_", " ")} delivery.
                  </div>
                  {data.channel !== "email" && (
                    <div className="text-[11px] text-slate-500 mt-1">
                      No transport configured for this channel yet. Confirming will mark recipients as sent so you can track replies manually.
                    </div>
                  )}
                </div>
                {!confirmSend ? (
                  <button
                    onClick={() => setConfirmSend(true)}
                    data-testid="campaign-send-trigger"
                    className="btn-primary text-sm inline-flex items-center gap-1.5"
                  >
                    <Send className="h-3.5 w-3.5" /> Send Now
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button
                      onClick={() => setConfirmSend(false)}
                      className="btn-ghost text-xs"
                      disabled={busy}
                    >Cancel</button>
                    <button
                      onClick={() => onSend()}
                      disabled={busy}
                      data-testid="campaign-send-confirm"
                      className="btn-primary text-sm inline-flex items-center gap-1.5"
                    >
                      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                      Confirm send to {queuedCount}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Recipients</div>
            <div className="ae-card overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-white/8">
                    <th className="text-left px-3 py-2.5">Owner</th>
                    <th className="text-left px-3 py-2.5">Contact</th>
                    <th className="text-left px-3 py-2.5">Status</th>
                    <th className="text-right px-3 py-2.5">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recipients.map((r) => {
                    const meta = STATUS_META[r.status] || STATUS_META.queued;
                    return (
                      <tr key={r.rid} data-testid={`recipient-row-${r.rid}`} className="border-b border-white/5">
                        <td className="px-3 py-2.5">
                          <div className="font-medium text-slate-200">{r.owner_name || "Unknown owner"}</div>
                          <div className="text-[10px] text-slate-500 font-mono truncate max-w-[180px]">{r.oid}</div>
                        </td>
                        <td className="px-3 py-2.5 text-xs text-slate-400 truncate max-w-[200px]">
                          {data.channel === "email" ? r.owner_email :
                            data.channel === "voicemail" ? r.owner_phone :
                            r.owner_name}
                          {!r.owner_email && data.channel === "email" && (
                            <span className="ml-1 text-amber-400 text-[10px]">no email</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5">
                          <span className={`inline-flex items-center gap-1.5 text-xs ${meta.color}`}>
                            <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
                            {meta.label}
                          </span>
                          {r.error && (
                            <div className="text-[10px] text-rose-400 mt-1 truncate max-w-[200px]" title={r.error}>
                              {r.error}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-right">
                          <div className="inline-flex gap-1">
                            {r.status === "sent" && (
                              <>
                                <button
                                  onClick={() => markStatus(r.rid, "replied")}
                                  data-testid={`mark-replied-${r.rid}`}
                                  className="text-[10px] px-2 py-1 rounded-sm border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10"
                                >
                                  <CheckCircle2 className="h-3 w-3 inline mr-1" /> Replied
                                </button>
                                <button
                                  onClick={() => markStatus(r.rid, "meeting_booked")}
                                  data-testid={`mark-meeting-${r.rid}`}
                                  className="text-[10px] px-2 py-1 rounded-sm border border-violet-500/30 text-violet-300 hover:bg-violet-500/10"
                                >
                                  <Calendar className="h-3 w-3 inline mr-1" /> Meeting
                                </button>
                              </>
                            )}
                            {r.status === "replied" && (
                              <button
                                onClick={() => markStatus(r.rid, "meeting_booked")}
                                data-testid={`mark-meeting-${r.rid}`}
                                className="text-[10px] px-2 py-1 rounded-sm border border-violet-500/30 text-violet-300 hover:bg-violet-500/10"
                              >
                                <Calendar className="h-3 w-3 inline mr-1" /> Meeting Booked
                              </button>
                            )}
                            {r.status === "failed" && (
                              <span className="text-[10px] text-rose-400 inline-flex items-center gap-1">
                                <AlertCircle className="h-3 w-3" /> Delivery failed
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Template</div>
            <pre className="ae-card p-4 text-[11.5px] font-mono whitespace-pre-wrap text-slate-200 leading-relaxed max-h-[280px] overflow-y-auto">{data.template}</pre>
          </div>
        </div>
      </div>
      {chOverride && (
        <ContactHoursOverrideModal
          count={chOverride.count}
          busy={busy}
          onCancel={() => setChOverride(null)}
          onConfirm={(justification) => onSend({ justification })}
        />
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  const color = {
    sky: "text-sky-300",
    emerald: "text-emerald-300",
    violet: "text-violet-300",
  }[accent] || "text-slate-200";
  return (
    <div className="ae-card p-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`font-mono text-2xl tabular-nums ${color}`}>{value}</div>
    </div>
  );
}

// =====================================================================
// Create Campaign (compose + targets)
// =====================================================================

function CreateCampaign({ opps, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [channel, setChannel] = useState("email");
  const [template, setTemplate] = useState(TEMPLATES_BY_CHANNEL.email[0].body);
  const [targets, setTargets] = useState([]);

  // Targeting filters
  const [filterText, setFilterText] = useState("");
  const [filterAsset, setFilterAsset] = useState("");
  const [filterStage, setFilterStage] = useState("");
  const [filterHasContact, setFilterHasContact] = useState(false);

  // AI Compose
  const [aiBrief, setAiBrief] = useState("");
  const [aiTone, setAiTone] = useState("warm");
  const [aiBusy, setAiBusy] = useState(false);

  // Preview
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);

  const [busy, setBusy] = useState(false);

  // Filter opps client-side
  const filteredOpps = useMemo(() => {
    let arr = opps;
    if (filterText) {
      const q = filterText.toLowerCase();
      arr = arr.filter((o) =>
        (o.title || "").toLowerCase().includes(q) ||
        (o.city || "").toLowerCase().includes(q) ||
        (o.state || "").toLowerCase().includes(q) ||
        (o.owner_name || "").toLowerCase().includes(q)
      );
    }
    if (filterAsset) arr = arr.filter((o) => o.type === filterAsset);
    if (filterStage) arr = arr.filter((o) => o.stage === filterStage);
    if (filterHasContact) {
      if (channel === "email") arr = arr.filter((o) => !!o.owner_email);
      else if (channel === "voicemail") arr = arr.filter((o) => !!o.owner_phone);
    }
    return arr;
  }, [opps, filterText, filterAsset, filterStage, filterHasContact, channel]);

  const stages = useMemo(() => {
    const s = new Set(opps.map((o) => o.stage).filter(Boolean));
    return Array.from(s).sort();
  }, [opps]);

  const toggleTarget = (oid) => {
    setTargets((arr) => arr.includes(oid) ? arr.filter((x) => x !== oid) : [...arr, oid]);
  };
  const selectAllFiltered = () => setTargets(filteredOpps.map((o) => o.oid));
  const clearTargets = () => setTargets([]);

  const onChannelChange = (val) => {
    setChannel(val);
    const first = TEMPLATES_BY_CHANNEL[val]?.[0];
    if (first && !template.trim()) setTemplate(first.body);
  };

  const applyTemplate = (body) => setTemplate(body);

  const insertTag = (tag) => {
    const insertion = `{${tag}}`;
    setTemplate((t) => t + (t.endsWith(" ") || !t ? "" : " ") + insertion);
  };

  const aiCompose = async () => {
    if (!aiBrief.trim()) {
      toast.error("Add a short brief first (e.g. 'tax-delinquent multifamily owners')");
      return;
    }
    setAiBusy(true);
    try {
      const r = await api.post("/outreach/ai-compose", {
        channel,
        brief: aiBrief.trim(),
        tone: aiTone,
        asset_class: filterAsset || null,
      });
      setTemplate(r.data.draft || "");
      toast.success("AI draft inserted. Edit before sending.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI compose failed");
    } finally {
      setAiBusy(false);
    }
  };

  const preview = async () => {
    if (!targets.length) { toast.error("Select at least one target to preview"); return; }
    setPreviewBusy(true);
    try {
      const r = await api.post("/outreach/preview", { template, oid: targets[0] });
      setPreviewData(r.data);
      setPreviewOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally {
      setPreviewBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Campaign name is required"); return; }
    if (!template.trim()) { toast.error("Template body is required"); return; }
    if (!targets.length) { toast.error("Select at least one target"); return; }
    setBusy(true);
    try {
      const r = await api.post("/outreach", {
        name: name.trim(),
        channel,
        template,
        target_oids: targets,
      });
      toast.success(`${targets.length} recipient${targets.length === 1 ? "" : "s"} queued. Open the campaign to send.`);
      onCreated(r.data.cid);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to queue campaign");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end items-start" data-testid="campaign-overlay">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <form
        onSubmit={submit}
        className="relative bg-[#05070A] border-l border-white/8 w-full max-w-4xl lg:max-w-[calc(56rem+96px)] h-full max-h-[calc(100vh-80px)] overflow-y-auto"
        data-testid="campaign-dialog"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-white/8 bg-[#05070A]/95 backdrop-blur">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-400">New campaign</div>
            <div className="font-heading text-xl tracking-tight mt-0.5">Compose outreach</div>
          </div>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-100 p-1.5" data-testid="campaign-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Campaign name *">
              <input
                data-testid="campaign-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="Q3 seller-financing pilot"
                className="inp"
              />
            </Field>
            <Field label="Channel">
              <select
                data-testid="campaign-channel"
                value={channel}
                onChange={(e) => onChannelChange(e.target.value)}
                className="inp"
              >
                {CHANNELS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </Field>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Templates</div>
              <div className="text-[10px] text-slate-500">Click to load. Edit freely.</div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {(TEMPLATES_BY_CHANNEL[channel] || []).map((t, i) => (
                <button
                  key={i}
                  type="button"
                  data-testid={`tpl-load-${channel}-${i}`}
                  onClick={() => applyTemplate(t.body)}
                  className="text-[11px] px-2.5 py-1 rounded-sm border border-white/10 hover:border-emerald-500/40 hover:bg-emerald-500/10 hover:text-emerald-300 text-slate-300"
                >
                  {t.name}
                </button>
              ))}
            </div>
          </div>

          <div className="ae-card p-4 border-emerald-500/20 bg-emerald-500/5 space-y-3" data-testid="ai-compose-panel">
            <div className="flex items-center gap-2 text-emerald-300 text-xs font-semibold">
              <Sparkles className="h-3.5 w-3.5" /> AI Compose
            </div>
            <div className="grid grid-cols-[1fr,140px] gap-2">
              <input
                data-testid="ai-compose-brief"
                value={aiBrief}
                onChange={(e) => setAiBrief(e.target.value)}
                placeholder="Brief: e.g. 'retiring HVAC owners open to seller financing'"
                className="inp"
              />
              <select
                data-testid="ai-compose-tone"
                value={aiTone}
                onChange={(e) => setAiTone(e.target.value)}
                className="inp"
              >
                <option value="warm">Warm</option>
                <option value="direct">Direct</option>
                <option value="formal">Formal</option>
              </select>
            </div>
            <button
              type="button"
              onClick={aiCompose}
              disabled={aiBusy}
              data-testid="ai-compose-btn"
              className="btn-primary text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              {aiBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
              {aiBusy ? "Drafting…" : "Generate Draft"}
            </button>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Template body</div>
              <div className="text-[10px] text-slate-500">{template.length} chars</div>
            </div>
            <textarea
              data-testid="campaign-template"
              value={template}
              onChange={(e) => setTemplate(e.target.value)}
              rows={10}
              placeholder="Paste or draft your message. Use {merge_tags} below to personalize."
              className="inp font-mono text-xs leading-relaxed"
              style={{ resize: "vertical", minHeight: "220px" }}
            />
            <div className="flex flex-wrap gap-1 mt-2">
              <span className="text-[10px] text-slate-500 mr-1">Merge tags:</span>
              {MERGE_TAGS.map((t) => (
                <button
                  key={t}
                  type="button"
                  data-testid={`merge-tag-${t}`}
                  onClick={() => insertTag(t)}
                  className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-slate-800/60 hover:bg-emerald-500/15 hover:text-emerald-300 text-slate-400 border border-white/5"
                >
                  {`{${t}}`}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Targets</div>
              <div className="text-xs text-slate-400" data-testid="target-count">
                {targets.length} selected · {filteredOpps.length} match filter
              </div>
            </div>

            <div className="grid grid-cols-[1fr,150px,150px,auto] gap-2 mb-2">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500" />
                <input
                  data-testid="target-search"
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                  placeholder="Search title, city, owner…"
                  className="inp pl-7"
                />
              </div>
              <select
                data-testid="target-asset"
                value={filterAsset}
                onChange={(e) => setFilterAsset(e.target.value)}
                className="inp"
              >
                <option value="">Any asset</option>
                <option value="business">Business</option>
                <option value="multifamily">Multifamily</option>
                <option value="land">Land</option>
              </select>
              <select
                data-testid="target-stage"
                value={filterStage}
                onChange={(e) => setFilterStage(e.target.value)}
                className="inp"
              >
                <option value="">Any stage</option>
                {stages.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
              </select>
              <label className="inline-flex items-center gap-1.5 text-[11px] text-slate-300 whitespace-nowrap">
                <input
                  data-testid="target-has-contact"
                  type="checkbox"
                  checked={filterHasContact}
                  onChange={(e) => setFilterHasContact(e.target.checked)}
                  className="accent-emerald-500"
                />
                Has {channel === "email" ? "email" : channel === "direct_mail" ? "mailing addr" : "phone"}
              </label>
            </div>

            <div className="flex items-center justify-between mb-1">
              <div className="flex gap-2">
                <button
                  type="button"
                  data-testid="target-select-all"
                  onClick={selectAllFiltered}
                  className="text-[11px] text-emerald-400 hover:text-emerald-300"
                >
                  Select all {filteredOpps.length} matching
                </button>
                <span className="text-slate-700">·</span>
                <button
                  type="button"
                  data-testid="target-clear"
                  onClick={clearTargets}
                  className="text-[11px] text-slate-400 hover:text-slate-200"
                >
                  Clear selection
                </button>
              </div>
              <button
                type="button"
                onClick={preview}
                disabled={!targets.length || previewBusy}
                data-testid="target-preview"
                className="text-[11px] inline-flex items-center gap-1 text-slate-300 hover:text-emerald-300 disabled:opacity-50"
              >
                {previewBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Eye className="h-3 w-3" />}
                Preview to first selected
              </button>
            </div>

            <div className="max-h-[280px] overflow-y-auto border border-white/8 rounded-sm divide-y divide-white/5" data-testid="target-list">
              {filteredOpps.length === 0 ? (
                <div className="px-4 py-8 text-center text-xs text-slate-500">No opportunities match these filters.</div>
              ) : filteredOpps.slice(0, 200).map((o) => (
                <label key={o.oid} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-white/[0.03] text-sm">
                  <input
                    type="checkbox"
                    data-testid={`target-${o.oid}`}
                    checked={targets.includes(o.oid)}
                    onChange={() => toggleTarget(o.oid)}
                    className="accent-emerald-500"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="truncate text-slate-200">{o.title}</div>
                    <div className="text-[10px] text-slate-500 inline-flex gap-2">
                      <span>{o.city}, {o.state}</span>
                      {o.type && <span className="capitalize">{o.type}</span>}
                      {channel === "email" && !o.owner_email && <span className="text-amber-400">no email</span>}
                    </div>
                  </div>
                </label>
              ))}
              {filteredOpps.length > 200 && (
                <div className="px-3 py-2 text-[10px] text-slate-500 text-center">
                  Showing first 200 of {filteredOpps.length}. Narrow filters to see more.
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 flex justify-end gap-2 px-6 py-4 border-t border-white/8 bg-[#05070A]/95 backdrop-blur">
          <button type="button" onClick={onClose} className="btn-ghost text-sm">Cancel</button>
          <button
            type="submit"
            disabled={busy || !targets.length}
            data-testid="campaign-submit"
            className="btn-primary text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            {busy ? "Queuing…" : `Queue ${targets.length || 0} recipient${targets.length === 1 ? "" : "s"}`}
          </button>
        </div>

        {previewOpen && previewData && (
          <PreviewModal data={previewData} channel={channel} onClose={() => setPreviewOpen(false)} />
        )}

        <style>{`
          .inp { width: 100%; background: #0A0D12; border: 1px solid rgba(255,255,255,0.1); border-radius: 4px; padding: 0.45rem 0.6rem; font-size: 0.85rem; outline: none; color: #F8FAFC; }
          .inp:focus { border-color: rgba(16,185,129,0.6); }
        `}</style>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
}

function PreviewModal({ data, channel, onClose }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-6 bg-black/70" data-testid="preview-modal">
      <div className="bg-white text-[#101010] rounded-sm shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-700">Preview</div>
            <div className="text-sm font-bold mt-0.5">Rendered for {data.context?.owner_name || "first recipient"}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="preview-close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          {channel === "email" && data.subject && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Subject</div>
              <div className="font-bold text-base mt-0.5">{data.subject}</div>
            </div>
          )}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Body</div>
            <pre className="font-sans text-sm whitespace-pre-wrap leading-relaxed mt-1 text-slate-800">{data.body}</pre>
          </div>
          <div className="text-[10px] text-slate-500 border-t border-slate-100 pt-3">
            Merge context: {Object.entries(data.context).filter(([, v]) => v).map(([k, v]) => `${k}=${v}`).join(" · ")}
          </div>
        </div>
      </div>
    </div>
  );
}


// =====================================================================
// BLOCK 100 Part E-7 ,  Contact-hour override (attestation + justification)
// =====================================================================

function ContactHoursOverrideModal({ window: win, count, busy, onCancel, onConfirm }) {
  const [attested, setAttested] = useState(false);
  const [justification, setJustification] = useState("");
  const canConfirm = attested && justification.trim().length >= 10 && !busy;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      data-testid="contact-hours-override-modal"
    >
      <div className="bg-[#0A0D12] border border-amber-500/40 rounded-sm shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between px-6 py-4 border-b border-white/8">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-amber-400 inline-flex items-center gap-1.5">
              <AlertCircle className="h-3 w-3" /> Outside legal contact hours
            </div>
            <div className="font-heading text-lg tracking-tight text-slate-100 mt-0.5">
              Contact-hour override
            </div>
          </div>
          <button onClick={onCancel} disabled={busy} className="text-slate-500 hover:text-slate-100 p-1.5" data-testid="contact-hours-override-cancel">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-sm text-slate-300 leading-relaxed">
            {count != null ? (
              <>
                <span className="font-semibold text-amber-300">{count}</span> recipient{count === 1 ? " is" : "s are"} currently
                outside the legally permitted contact window for their state. TCPA rules restrict
                automated outreach to the recipient's local daytime hours.
              </>
            ) : (
              <>This recipient is currently outside the legally permitted contact window for their state.
              TCPA rules restrict automated outreach to the recipient's local daytime hours.</>
            )}
          </p>

          {win && (
            <div className="ae-card p-3 border-white/10 bg-white/[0.02] text-xs space-y-2" data-testid="contact-hours-window-detail">
              <div className="flex justify-between">
                <span className="text-slate-500 uppercase tracking-widest text-[10px]">Recipient state</span>
                <span className="text-slate-200 font-mono">{win.state || ", "}</span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-slate-500 uppercase tracking-widest text-[10px]">Legal window</span>
                <span className="text-slate-200 text-right">{win.window_label}</span>
              </div>
              {(win.checks || []).map((c) => (
                <div key={c.tz} className="flex justify-between gap-3 border-t border-white/5 pt-1.5">
                  <span className="text-slate-500 font-mono text-[10px]">{c.tz}</span>
                  <span className={`font-mono text-[10px] ${c.ok ? "text-emerald-300" : "text-rose-300"}`}>
                    {c.weekday} {new Date(c.local_time).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })} · {c.window}
                  </span>
                </div>
              ))}
            </div>
          )}

          <label className="flex items-start gap-2.5 text-sm text-slate-200 cursor-pointer">
            <input
              type="checkbox"
              checked={attested}
              onChange={(e) => setAttested(e.target.checked)}
              className="accent-amber-500 mt-0.5"
              data-testid="contact-hours-attest-checkbox"
            />
            <span>
              I attest that I have a lawful basis to contact this recipient outside the standard
              window (e.g. prior express consent or an established business relationship) and take
              responsibility for this send.
            </span>
          </label>

          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">
              Justification (required, min 10 chars)
            </label>
            <textarea
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              rows={3}
              placeholder="Document the lawful basis for contacting outside the legal window…"
              data-testid="contact-hours-justification"
              className="w-full bg-[#05070A] border border-white/10 rounded-sm px-3 py-2 text-sm text-slate-100 outline-none focus:border-amber-500/60"
              style={{ resize: "vertical" }}
            />
          </div>

          <p className="text-[10px] text-slate-500">
            This override and your justification are permanently recorded to the audit log with a full timestamp.
          </p>
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-white/8">
          <button onClick={onCancel} disabled={busy} className="btn-ghost text-sm">Cancel</button>
          <button
            onClick={() => onConfirm(justification.trim())}
            disabled={!canConfirm}
            data-testid="contact-hours-override-confirm"
            className="text-sm inline-flex items-center gap-1.5 px-4 py-2 rounded-sm bg-amber-500/90 hover:bg-amber-500 text-black font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
            Attest & Send
          </button>
        </div>
      </div>
    </div>
  );
}
